import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useStore } from '../context/StoreContext';
import SafeIcon from '../common/SafeIcon';
import { QUICK_CAPTURE_AUTO_SAVE_MS } from '../constants';
import { processAttachment } from '../utils/attachment';
import { uploadAttachmentSilently } from '../utils/cloudAttachment';
import {
  clearClipboardHistory,
  copyText,
  getClipboardHistory,
  rememberClipboard
} from '../utils/productivity';

const SHARE_CACHE = 'stickyhq-share-target-v1';

const getTimestamp = value => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getSelectedText = () => {
  const active = document.activeElement;
  if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) {
    const start = active.selectionStart;
    const end = active.selectionEnd;
    if (Number.isInteger(start) && Number.isInteger(end) && end > start) return active.value.slice(start, end);
  }
  return window.getSelection?.().toString() || '';
};

const QuickCapture = ({ isMobile, showFab = true }) => {
  const {
    createNote, saveNote, setEditingNote, selectedNoteIds,
    notes, workspaces, templates, syncState, runSync, isSyncing
  } = useStore();
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState('capture');
  const [draft, setDraft] = useState({ title: '', bodyText: '' });
  const [clipboardHistory, setClipboardHistory] = useState(() => getClipboardHistory());
  const [clipboardMessage, setClipboardMessage] = useState('');
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const timerRef = useRef(null);
  const shareHandledRef = useRef(false);

  const lastSyncAt = Number(syncState?.lastSyncAt || 0);
  const pendingChanges = [...notes, ...workspaces, ...templates]
    .filter(item => getTimestamp(item?.updatedAt) > lastSyncAt)
    .length;

  useEffect(() => {
    const onPaste = e => {
      const text = e.clipboardData?.getData('text/plain');
      if (text?.trim()) rememberClipboard(text, { source: 'paste' });
    };
    const onCopy = () => {
      const text = getSelectedText();
      if (text?.trim()) rememberClipboard(text, { source: 'copy' });
    };
    const onClipboardChanged = e => setClipboardHistory(e.detail || getClipboardHistory());
    const onOpenCapture = () => { setTab('capture'); setClipboardMessage(''); setIsOpen(true); };
    const onOpenHistory = () => { setTab('history'); setClipboardMessage(''); setIsOpen(true); };
    document.addEventListener('paste', onPaste);
    document.addEventListener('copy', onCopy);
    window.addEventListener('stickyhq:clipboard-history-changed', onClipboardChanged);
    window.addEventListener('stickyhq:open-quick-capture', onOpenCapture);
    window.addEventListener('stickyhq:open-clipboard-history', onOpenHistory);
    return () => {
      document.removeEventListener('paste', onPaste);
      document.removeEventListener('copy', onCopy);
      window.removeEventListener('stickyhq:clipboard-history-changed', onClipboardChanged);
      window.removeEventListener('stickyhq:open-quick-capture', onOpenCapture);
      window.removeEventListener('stickyhq:open-clipboard-history', onOpenHistory);
    };
  }, []);

  useEffect(() => {
    const onOnline = () => {
      setIsOnline(true);
      // Use a dedicated trigger so reconnect sync is not suppressed by the
      // normal activation/focus throttle.
      void runSync('online');
    };
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [runSync]);

  useEffect(() => {
    if (shareHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const shareToken = params.get('share_token');
    const legacyTitle = params.get('share_title') || '';
    const legacyText = params.get('share_text') || '';
    const legacyUrl = params.get('share_url') || '';
    if (!shareToken && !legacyTitle && !legacyText && !legacyUrl) return;

    shareHandledRef.current = true;
    void (async () => {
      let title = legacyTitle;
      let text = legacyText;
      let url = legacyUrl;
      const attachments = [];

      if (shareToken && 'caches' in window) {
        try {
          const cache = await caches.open(SHARE_CACHE);
          const prefix = `/__stickyhq_share/${encodeURIComponent(shareToken)}`;
          const metaPath = `${prefix}/meta`;
          const metaResponse = await cache.match(metaPath);
          if (metaResponse) {
            const metadata = await metaResponse.json();
            title = String(metadata.title || '');
            text = String(metadata.text || '');
            url = String(metadata.url || '');

            for (const fileMeta of metadata.files || []) {
              const fileResponse = await cache.match(fileMeta.path);
              if (!fileResponse) continue;
              const blob = await fileResponse.blob();
              const file = new File([blob], fileMeta.name || 'shared-image', {
                type: fileMeta.type || blob.type || 'application/octet-stream'
              });
              try {
                attachments.push(await processAttachment(file));
              } catch (err) {
                console.warn('Shared image skipped', fileMeta.name, err);
              }
              await cache.delete(fileMeta.path);
            }
            await cache.delete(metaPath);
          }
        } catch (err) {
          console.warn('Could not consume shared content', err);
        }
      }

      const bodyText = [text, url].filter(Boolean).join('\n\n');
      const shared = createNote('text', {
        title: title || (attachments.length ? 'Shared screenshot' : url ? 'Shared link' : 'Shared capture'),
        bodyText,
        attachments,
        inbox: true,
        createdViaShareTarget: true
      });
      if (bodyText) rememberClipboard(bodyText, { source: 'share-target' });
      setEditingNote(shared);

      if (attachments.length) {
        const cloudAttachments = [];
        for (const attachment of attachments) {
          cloudAttachments.push(await uploadAttachmentSilently(attachment, shared.id));
        }
        const saved = await saveNote({ ...shared, attachments: cloudAttachments }, true);
        setEditingNote(saved);
      }

      window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
    })();
  }, [createNote, saveNote, setEditingNote]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  if (isMobile && selectedNoteIds.length > 0) return null;

  const close = () => {
    setIsOpen(false);
    setTab('capture');
    setClipboardMessage('');
    setDraft({ title: '', bodyText: '' });
  };

  const persistDraft = next => {
    if (!next.title.trim() && !next.bodyText.trim()) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      if (next.id) {
        const saved = await saveNote(next, true);
        setDraft(saved);
      } else {
        const created = createNote('text', {
          title: next.title,
          bodyText: next.bodyText,
          inbox: true,
          createdViaQuickCapture: true
        });
        setEditingNote(null);
        setDraft(created);
      }
    }, QUICK_CAPTURE_AUTO_SAVE_MS);
  };

  const handleUpdate = updates => {
    const next = { ...draft, ...updates };
    setDraft(next);
    persistDraft(next);
  };

  const importCurrentClipboard = async (insertIntoDraft = false) => {
    setClipboardMessage('');
    try {
      const text = await navigator.clipboard.readText();
      if (!text?.trim()) {
        setClipboardMessage('The current clipboard does not contain readable text.');
        return false;
      }
      rememberClipboard(text, { source: 'clipboard-import' });
      if (insertIntoDraft) {
        handleUpdate({ bodyText: [draft.bodyText, text].filter(Boolean).join(draft.bodyText ? '\n\n' : '') });
      } else {
        setClipboardMessage('Current clipboard item added to StickyHQ history.');
      }
      return true;
    } catch (err) {
      console.warn('Clipboard read unavailable', err);
      setClipboardMessage('Clipboard access was blocked by the browser. Click the page and try again, or paste with Ctrl/Cmd+V.');
      return false;
    }
  };

  const pasteClipboard = () => importCurrentClipboard(true);

  const insertHistoryItem = item => {
    setTab('capture');
    handleUpdate({ bodyText: [draft.bodyText, item.text].filter(Boolean).join(draft.bodyText ? '\n\n' : '') });
  };

  const createFromHistory = item => {
    const note = createNote('text', {
      title: item.text.split('\n')[0].slice(0, 80) || 'Clipboard capture',
      bodyText: item.text,
      inbox: true,
      createdViaClipboardHistory: true
    });
    setEditingNote(note);
    setIsOpen(false);
  };

  return (
    <div className={`fixed z-[80] w-14 h-14 ${isMobile ? 'bottom-[calc(76px+env(safe-area-inset-bottom))] right-3' : 'bottom-8 right-8'}`}>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 16 }}
            className={`absolute ${showFab ? 'bottom-20' : 'bottom-0'} right-0 ${isMobile ? 'w-[calc(100vw-24px)] max-w-[430px]' : 'w-[390px]'} max-h-[min(72dvh,620px)] bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden`}
          >
            <div className="p-3 border-b border-gray-100 flex items-center gap-2">
              <button onClick={() => { setTab('capture'); setClipboardMessage(''); }} className={`flex-1 min-h-11 py-2 rounded-xl text-sm font-bold flex items-center justify-center gap-2 ${tab === 'capture' ? 'bg-blue-50 text-blue-700' : 'text-gray-500 hover:bg-gray-50'}`}>
                <SafeIcon name="Zap" /> Capture
              </button>
              <button onClick={() => { setTab('history'); setClipboardMessage(''); }} className={`flex-1 min-h-11 py-2 rounded-xl text-sm font-bold flex items-center justify-center gap-2 ${tab === 'history' ? 'bg-blue-50 text-blue-700' : 'text-gray-500 hover:bg-gray-50'}`}>
                <SafeIcon name="Clipboard" /> Clipboard {clipboardHistory.length > 0 && <span className="text-[10px] bg-white px-1.5 py-0.5 rounded-full">{clipboardHistory.length}</span>}
              </button>
              <button onClick={close} className="min-w-11 min-h-11 p-2 text-gray-400 hover:bg-gray-100 rounded-xl"><SafeIcon name="X" /></button>
            </div>

            {tab === 'capture' ? (
              <div className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-amber-600 bg-amber-50 px-2 py-1 rounded-lg">Capture Inbox</span>
                  <button onClick={() => void pasteClipboard()} className="min-h-11 text-sm font-bold text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-xl flex items-center gap-1.5"><SafeIcon name="Clipboard" /> Paste current</button>
                </div>
                <input
                  autoFocus
                  placeholder="Title…"
                  value={draft.title || ''}
                  onChange={e => handleUpdate({ title: e.target.value })}
                  className="w-full font-black text-gray-900 placeholder-gray-300 focus:outline-none text-lg"
                />
                <textarea
                  placeholder="Type or paste anything here…"
                  value={draft.bodyText || ''}
                  onChange={e => handleUpdate({ bodyText: e.target.value })}
                  className="w-full h-44 text-sm text-gray-600 placeholder-gray-300 focus:outline-none resize-none leading-relaxed"
                />
                <div className="flex gap-2">
                  <button onClick={() => setTab('history')} className="min-w-12 min-h-12 p-3 bg-gray-100 text-gray-600 rounded-2xl font-bold"><SafeIcon name="Clock" /></button>
                  <button onClick={close} className="flex-1 min-h-12 py-3.5 bg-gray-900 text-white rounded-2xl text-base font-bold hover:bg-black transition">Done</button>
                </div>
              </div>
            ) : (
              <div className="max-h-[460px] overflow-y-auto p-3">
                <div className="px-2 py-2 flex items-center justify-between gap-3">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">StickyHQ clipboard history</span>
                  {clipboardHistory.length > 0 && <button onClick={clearClipboardHistory} className="text-[10px] font-bold text-red-400 hover:text-red-600">Clear</button>}
                </div>
                <div className="mx-2 mb-3 rounded-xl bg-blue-50 border border-blue-100 p-3 text-[11px] leading-relaxed text-blue-800">
                  StickyHQ can remember items copied or pasted while you use this app. Your Windows/macOS clipboard history is separate and browsers do not expose that historical list to a normal web app.
                  <button onClick={() => void importCurrentClipboard(false)} className="mt-2 w-full min-h-11 py-2 rounded-xl bg-white border border-blue-200 text-blue-700 font-bold flex items-center justify-center gap-2"><SafeIcon name="Download" /> Import current clipboard item</button>
                  {clipboardMessage && <div className="mt-2 text-blue-700">{clipboardMessage}</div>}
                </div>
                {clipboardHistory.length === 0 ? (
                  <div className="py-10 text-center text-gray-400"><SafeIcon name="Clipboard" className="text-3xl mx-auto mb-3 opacity-30" /><div className="text-sm font-medium">No StickyHQ clipboard items yet</div><div className="text-xs mt-1">Copy or paste text inside StickyHQ, or import the current clipboard item above.</div></div>
                ) : clipboardHistory.map(item => (
                  <div key={item.id} className="p-3 rounded-2xl hover:bg-gray-50 border border-transparent hover:border-gray-100 group">
                    <div className="text-sm text-gray-700 line-clamp-3 whitespace-pre-wrap">{item.text}</div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className="text-[10px] uppercase tracking-wider text-gray-400">{new Date(item.createdAt).toLocaleString()}</span>
                      <div className="flex gap-1 flex-wrap justify-end">
                        <button onClick={() => void copyText(item.text, 'clipboard-history')} className="min-w-11 min-h-11 p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl" title="Copy again"><SafeIcon name="Copy" /></button>
                        <button onClick={() => insertHistoryItem(item)} className="min-h-11 px-3 py-2 text-xs font-bold text-blue-600 bg-blue-50 rounded-xl">Insert</button>
                        <button onClick={() => createFromHistory(item)} className="min-h-11 px-3 py-2 text-xs font-bold text-gray-700 bg-gray-100 rounded-xl">New note</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {pendingChanges > 0 && (
        <button
          type="button"
          onClick={() => { if (isOnline && !isSyncing) void runSync('pending_badge'); }}
          className={`absolute right-0 bottom-16 whitespace-nowrap px-3 py-1.5 rounded-full shadow-lg text-[10px] font-bold flex items-center gap-1.5 ${isOnline ? 'bg-white border border-gray-200 text-gray-600' : 'bg-gray-900 text-white'}`}
          title={isOnline ? 'Click to sync pending changes' : 'Changes are safely stored locally and will sync when you reconnect'}
        >
          <SafeIcon name={isOnline ? (isSyncing ? 'RefreshCw' : 'Cloud') : 'WifiOff'} className={isSyncing ? 'animate-spin' : ''} />
          {isOnline ? `${pendingChanges} change${pendingChanges === 1 ? '' : 's'} pending` : `Offline · ${pendingChanges} pending`}
        </button>
      )}

      {showFab && (
        <motion.button
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.94 }}
          onClick={() => isOpen ? close() : setIsOpen(true)}
          className={`w-14 h-14 rounded-full shadow-2xl flex items-center justify-center text-white transition-colors safe-bottom-margin ${isOpen ? 'bg-red-500 rotate-45' : 'bg-blue-600'}`}
          title="Quick Capture"
        >
          <SafeIcon name="Plus" className="text-2xl" />
        </motion.button>
      )}
    </div>
  );
};

export default QuickCapture;